import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  GripVertical,
  ListTree,
  MoreHorizontal,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  isWbsItemBacklogLinked,
  isWbsItemShadow,
  primaryBacklogIssueKey,
  primaryBacklogProjectKey,
  readWbsItemSystems,
  type AssigneeDoc,
  type WbsItemDoc,
  type WbsItemStatus,
} from '@mct/shared';
import { cn } from '../../lib/utils';
import { useDismissable } from '../../lib/useDismissable';
import { tsToDate } from '../../lib/wbsGantt';
import { buildBacklogIssueUrl } from '../../lib/backlog';
import { classifyDelay, type WbsTreeNode } from '../../lib/wbsTree';
import {
  classifyDropPosition,
  type DropPosition,
} from '../../lib/wbsResort';
import type { UpdateWbsItemPatch } from '../../lib/wbs';
import { Badge } from '../ui/Badge';
import { BOTTOM_PAD_PX, PHASE_GAP_PX, ROW_HEIGHT } from './WbsGantt';
import { resolveAssigneeColorClasses } from '../../lib/assigneeColor';
import { AssigneeChip } from '../common/AssigneeChip';
import { areaBadgeClasses, type AreaColorIndexMap } from '../../lib/areaColor';
import type { WbsColumnKey } from '../../lib/wbsColumnWidths';
import {
  InlineChoice,
  InlineDate,
  InlineMultiChoice,
  InlineText,
  type ChoiceOption,
} from './InlineEditors';
import { createEditableCellClickHandler } from './wbsRowClick';

const STATUS_LABEL: Record<WbsItemStatus, string> = {
  not_started: '未着手',
  in_progress: '進行中',
  done: '完了',
  blocked: 'ブロック',
  cancelled: '中止',
};

const STATUS_TONE: Record<
  WbsItemStatus,
  'slate' | 'green' | 'yellow' | 'red' | 'blue'
> = {
  not_started: 'slate',
  in_progress: 'yellow',
  done: 'green',
  blocked: 'red',
  cancelled: 'slate',
};

/**
 * Choices for the inline status picker. Tailwind classes mirror the
 * Badge tones so the popover row visually previews the result.
 */
const STATUS_CHOICES: readonly ChoiceOption[] = [
  {
    value: 'not_started',
    label: '未着手',
    badgeClasses: 'bg-slate-100 text-slate-700 border border-slate-200',
  },
  {
    value: 'in_progress',
    label: '進行中',
    badgeClasses: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
  },
  {
    value: 'done',
    label: '完了',
    badgeClasses: 'bg-green-100 text-green-800 border border-green-200',
  },
  {
    value: 'blocked',
    label: 'ブロック',
    badgeClasses: 'bg-red-100 text-red-800 border border-red-200',
  },
  {
    value: 'cancelled',
    label: '中止',
    badgeClasses: 'bg-slate-100 text-slate-500 border border-slate-200',
  },
];

export type EditableField =
  | 'name'
  | 'system'
  | 'assigneeId'
  | 'plannedStart'
  | 'plannedEnd'
  | 'status';

/**
 * Inline-editable fields that Backlog masters once a row links a Backlog
 * issue. Editing these from the WBS tree is blocked (Backlog is the source
 * of truth).
 */
const BACKLOG_LOCKED_TREE_FIELDS: ReadonlySet<EditableField> = new Set([
  'name',
  'system',
  'assigneeId',
  'plannedStart',
  'plannedEnd',
  'status',
]);

interface EditTarget {
  itemId: string;
  field: EditableField;
  anchorRect: DOMRect | null;
}

/**
 * Convert a Date to a `YYYY-MM-DD` string suitable for `<input type="date">`.
 * Mirrors the JST-normalized format used by WbsItemDetailPanel so both
 * editors round-trip the same value.
 */
function toDateInput(d: Date | null): string {
  if (!d) return '';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseDateInput(v: string): Date | null {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

export interface DropTarget {
  targetId: string;
  position: DropPosition;
  invalid: boolean;
}

export interface DragIntent {
  draggedId: string;
  target: DropTarget | null;
}

interface Props {
  nodes: readonly FlatNode[];
  expanded: ReadonlySet<string>;
  selectedId: string | null;
  hoveredId?: string | null;
  now: Date;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
  onHover?: (id: string | null) => void;
  /** Called at the end of a drag with the final drop intent. */
  onReorder?: (intent: {
    draggedId: string;
    targetId: string;
    position: DropPosition;
  }) => void;
  /**
   * Predicate: does the given intent resolve to a valid move? Used to gate
   * the drop zone styling (red/cross-out for invalid). If omitted all drops
   * are considered valid.
   */
  isDropValid?: (
    draggedId: string,
    targetId: string,
    position: DropPosition,
  ) => boolean;
  /**
   * Column layout driven from the parent so WbsPage can size its
   * sticky-left container to match the tree's total width and stop
   * the tree from overflowing into the Gantt area.
   */
  gridTemplate: string;
  beginResize: (key: WbsColumnKey) => (e: React.MouseEvent) => void;
  /**
   * Expand / collapse actions surfaced by the 名称 header dropdown. The
   * tree owns the menu UI (trigger + popover) but the parent owns the
   * `expanded` set, so the actual set mutations live in WbsPage and are
   * passed in as callbacks.
   *   - `onExpandAll` — expand every row.
   *   - `onCollapseAll` — collapse every row.
   *   - `onCollapsePhaseParents` — keep phases open, collapse the parent
   *     tasks directly under them ("フェーズ内親タスクのみ折りたたむ").
   */
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onCollapsePhaseParents: () => void;
  /**
   * Which editable-options popover is open (or null). The tree owns
   * the ⋯ button anchoring; the parent controls which popover is
   * rendered so that save handling + data migration can live there.
   */
  openEditor: 'system' | 'assignee' | null;
  onOpenEditor: (key: 'system' | 'assignee' | null) => void;
  /** Popover UI element to render under the active header. */
  editorSlot?: React.ReactNode;
  /** Option catalogs for the inline dropdown editors. */
  areaOptions: readonly string[];
  /**
   * `area-label -> stored colorIndex`. When an area is in the map we
   * render its row badge / picker swatch with that explicit colour;
   * otherwise the badge falls back to the deterministic name hash so
   * legacy projects (no `WbsCatalogOption.colorIndex`) keep rendering.
   */
  areaColorIndices?: AreaColorIndexMap;
  /**
   * Project-wide assignee list (the same list shared by the
   * migration-task DAG view and the Settings page). The picker writes
   * the selected `assigneeId` back to the row; the cell renders the
   * doc's name + frozen color.
   */
  assigneeOptions: readonly AssigneeDoc[];
  /** Persist a single-field edit to Firestore. */
  onEdit: (itemId: string, patch: UpdateWbsItemPatch) => Promise<void> | void;
  /**
   * Backlog space URL from `project.backlog.spaceUrl`. When present, rows
   * with `backlogIssueKeys` show a small jump-to-Backlog link icon next
   * to the name. `null` when the project has no Backlog config.
   */
  backlogSpaceUrl: string | null;
  /**
   * Outer scrolling ancestor. When provided, the tree drives auto-scroll
   * against it while the cursor is dragged near the top / bottom edges so
   * off-screen rows can be reached without releasing the mouse.
   */
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
}

export interface FlatNode {
  node: WbsTreeNode;
  hasHiddenChildren: boolean;
  directMatch: boolean;
}

interface RowSnapshot {
  /** Row top, relative to the tree container's content origin. */
  top: number;
  bottom: number;
  height: number;
  depth: number;
  /** Row left offset in px from the container (for indent math). */
  left: number;
}

interface Indicator {
  kind: 'line' | 'inside';
  /** Content-space Y (px from container top) for 'line', or row top for 'inside'. */
  top: number;
  /** Row height (for 'inside') — ignored for 'line'. */
  height: number;
  /** Left inset of the line (indent hint). Ignored for 'inside'. */
  leftIndent: number;
  invalid: boolean;
}

const AUTO_SCROLL_EDGE_PX = 48;
const AUTO_SCROLL_MAX_PX_PER_FRAME = 14;

export function WbsTreeTable({
  nodes,
  expanded,
  selectedId,
  hoveredId,
  now,
  onToggle,
  onSelect,
  onHover,
  onReorder,
  isDropValid,
  gridTemplate,
  beginResize,
  onExpandAll,
  onCollapseAll,
  onCollapsePhaseParents,
  openEditor,
  onOpenEditor,
  editorSlot,
  areaOptions,
  areaColorIndices,
  assigneeOptions,
  onEdit,
  backlogSpaceUrl,
  scrollContainerRef,
}: Props) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [indicator, setIndicator] = useState<Indicator | null>(null);
  const [editing, setEditing] = useState<EditTarget | null>(null);
  /** Transient toast shown when a user clicks a Backlog-locked cell. */
  const [backlogLockNotice, setBacklogLockNotice] = useState<string | null>(
    null,
  );
  const lockNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Live DOM refs for each rendered row, keyed by itemId. */
  const rowRectsRef = useRef(new Map<string, HTMLDivElement>());
  /**
   * Row rects captured at drag-start, stored in container-content
   * coordinates so they remain valid as the outer scroll container
   * moves under the cursor. Rows never move within the container (we
   * don't apply sortable transforms), so `top/bottom` are stable until
   * the drag ends.
   */
  const snapshotRef = useRef<Map<string, RowSnapshot>>(new Map());
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const dropTargetRef = useRef<DropTarget | null>(null);
  const rafRef = useRef<number | null>(null);
  const pointerMoveCleanupRef = useRef<(() => void) | null>(null);
  const isDropValidRef = useRef(isDropValid);
  useEffect(() => {
    isDropValidRef.current = isDropValid;
  }, [isDropValid]);
  useEffect(
    () => () => {
      if (lockNoticeTimerRef.current) clearTimeout(lockNoticeTimerRef.current);
    },
    [],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const activeNode = useMemo(
    () => nodes.find((n) => n.node.item.itemId === activeId) ?? null,
    [nodes, activeId],
  );

  const stopLoop = () => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pointerMoveCleanupRef.current?.();
    pointerMoveCleanupRef.current = null;
  };

  useEffect(() => () => stopLoop(), []);

  const onDragStart = (e: DragStartEvent) => {
    const draggedId = String(e.active.id);
    const containerEl = containerRef.current;
    if (!containerEl) return;
    const cbox = containerEl.getBoundingClientRect();
    const snap = new Map<string, RowSnapshot>();
    for (const [id, el] of rowRectsRef.current) {
      if (!el.isConnected) continue;
      const r = el.getBoundingClientRect();
      const node = nodes.find((n) => n.node.item.itemId === id);
      snap.set(id, {
        top: r.top - cbox.top,
        bottom: r.bottom - cbox.top,
        height: r.height,
        depth: node?.node.depth ?? 0,
        left: r.left - cbox.left,
      });
    }
    snapshotRef.current = snap;

    // Seed the pointer with the activation event position so the first
    // tick has a valid coord even before the user's pointer moves.
    const activator = e.activatorEvent as Partial<PointerEvent> | null;
    if (activator && typeof activator.clientX === 'number') {
      pointerRef.current = {
        x: activator.clientX,
        y: activator.clientY ?? 0,
      };
    }
    dropTargetRef.current = null;
    setActiveId(draggedId);
    setIndicator(null);

    const onMove = (ev: PointerEvent) => {
      pointerRef.current = { x: ev.clientX, y: ev.clientY };
    };
    window.addEventListener('pointermove', onMove);
    pointerMoveCleanupRef.current = () =>
      window.removeEventListener('pointermove', onMove);

    const tick = () => {
      runDragFrame(draggedId);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  /**
   * One frame of drag-loop work: runs at the browser's animation cadence
   * so auto-scroll and hit-testing stay synchronized even when the
   * pointer is held still at an edge.
   */
  const runDragFrame = (draggedId: string) => {
    const pointer = pointerRef.current;
    const containerEl = containerRef.current;
    if (!pointer || !containerEl) return;

    // Auto-scroll against the outer container (if any) before hit-testing
    // so the content-space coord we compute is consistent with what the
    // user now sees on screen.
    const scrollEl = scrollContainerRef?.current ?? null;
    if (scrollEl) {
      const sbox = scrollEl.getBoundingClientRect();
      let dy = 0;
      const topDist = pointer.y - sbox.top;
      const bottomDist = sbox.bottom - pointer.y;
      if (topDist < AUTO_SCROLL_EDGE_PX) {
        dy = -Math.max(
          2,
          Math.round(
            AUTO_SCROLL_MAX_PX_PER_FRAME *
              (1 - Math.max(0, topDist) / AUTO_SCROLL_EDGE_PX),
          ),
        );
      } else if (bottomDist < AUTO_SCROLL_EDGE_PX) {
        dy = Math.max(
          2,
          Math.round(
            AUTO_SCROLL_MAX_PX_PER_FRAME *
              (1 - Math.max(0, bottomDist) / AUTO_SCROLL_EDGE_PX),
          ),
        );
      }
      if (dy !== 0) {
        scrollEl.scrollTop = Math.max(
          0,
          Math.min(
            scrollEl.scrollHeight - scrollEl.clientHeight,
            scrollEl.scrollTop + dy,
          ),
        );
      }
    }

    const cbox = containerEl.getBoundingClientRect();
    const contentY = pointer.y - cbox.top;
    const contentX = pointer.x - cbox.left;

    let bestTarget: DropTarget | null = null;
    let bestSnap: RowSnapshot | null = null;
    let topId: string | null = null;
    let topSnap: RowSnapshot | null = null;
    let bottomId: string | null = null;
    let bottomSnap: RowSnapshot | null = null;
    for (const [itemId, snap] of snapshotRef.current) {
      if (itemId === draggedId) continue;
      if (!topSnap || snap.top < topSnap.top) {
        topSnap = snap;
        topId = itemId;
      }
      if (!bottomSnap || snap.bottom > bottomSnap.bottom) {
        bottomSnap = snap;
        bottomId = itemId;
      }
      if (contentY < snap.top || contentY > snap.bottom) continue;
      const position = classifyDropPosition({
        offsetY: contentY - snap.top,
        rowHeight: snap.height,
        offsetX: contentX,
        targetDepth: snap.depth,
      });
      const invalid = isDropValidRef.current
        ? !isDropValidRef.current(draggedId, itemId, position)
        : false;
      bestTarget = { targetId: itemId, position, invalid };
      bestSnap = snap;
    }

    // Fallback: when the cursor is in a dead zone outside every row
    // (e.g. behind the sticky header for the topmost row, or below the
    // last row in the empty space), clamp to the nearest edge so the
    // user can still drop "before topmost" / "after bottommost". Without
    // this, the topmost row's "before" zone is unreachable because the
    // sticky header occludes its top third.
    if (!bestTarget) {
      if (topSnap && topId && contentY < topSnap.top) {
        const invalid = isDropValidRef.current
          ? !isDropValidRef.current(draggedId, topId, 'before')
          : false;
        bestTarget = { targetId: topId, position: 'before', invalid };
        bestSnap = topSnap;
      } else if (bottomSnap && bottomId && contentY > bottomSnap.bottom) {
        const invalid = isDropValidRef.current
          ? !isDropValidRef.current(draggedId, bottomId, 'after')
          : false;
        bestTarget = { targetId: bottomId, position: 'after', invalid };
        bestSnap = bottomSnap;
      }
    }

    dropTargetRef.current = bestTarget;

    if (!bestTarget || !bestSnap) {
      setIndicator((prev) => (prev === null ? prev : null));
      return;
    }
    const indentPx = 24;
    const leftIndent =
      bestTarget.position === 'inside'
        ? 0
        : Math.min(
            bestSnap.left + (bestSnap.depth + 1) * indentPx,
            cbox.width - 40,
          );
    let next: Indicator;
    if (bestTarget.position === 'inside') {
      next = {
        kind: 'inside',
        top: bestSnap.top,
        height: bestSnap.height,
        leftIndent: 0,
        invalid: bestTarget.invalid,
      };
    } else {
      const y =
        bestTarget.position === 'before' ? bestSnap.top : bestSnap.bottom;
      next = {
        kind: 'line',
        top: y,
        height: 0,
        leftIndent,
        invalid: bestTarget.invalid,
      };
    }
    setIndicator((prev) =>
      prev &&
      prev.kind === next.kind &&
      prev.top === next.top &&
      prev.height === next.height &&
      prev.leftIndent === next.leftIndent &&
      prev.invalid === next.invalid
        ? prev
        : next,
    );
  };

  const onDragEnd = (_e: DragEndEvent) => {
    const dragged = activeId;
    const target = dropTargetRef.current;
    stopLoop();
    snapshotRef.current = new Map();
    pointerRef.current = null;
    dropTargetRef.current = null;
    setActiveId(null);
    setIndicator(null);
    if (!dragged || !target || target.invalid) return;
    onReorder?.({
      draggedId: dragged,
      targetId: target.targetId,
      position: target.position,
    });
  };

  const onDragCancel = () => {
    stopLoop();
    snapshotRef.current = new Map();
    pointerRef.current = null;
    dropTargetRef.current = null;
    setActiveId(null);
    setIndicator(null);
  };

  const registerRow = (id: string, el: HTMLDivElement | null) => {
    if (el) rowRectsRef.current.set(id, el);
    else rowRectsRef.current.delete(id);
  };

  const openEdit = (
    itemId: string,
    field: EditableField,
    e: React.MouseEvent,
  ) => {
    // Backlog-mastered rows: block the editor for synced fields and show
    // a transient toast pointing the user to Backlog.
    const target = nodes.find((n) => n.node.item.itemId === itemId)?.node.item;
    if (
      target &&
      isWbsItemBacklogLinked(target) &&
      BACKLOG_LOCKED_TREE_FIELDS.has(field)
    ) {
      const key = primaryBacklogIssueKey(target);
      setBacklogLockNotice(
        `Backlog 連携中 (${key ?? '課題'}) — 編集は Backlog で行ってください`,
      );
      if (lockNoticeTimerRef.current) clearTimeout(lockNoticeTimerRef.current);
      lockNoticeTimerRef.current = setTimeout(
        () => setBacklogLockNotice(null),
        3200,
      );
      return;
    }
    // Shadow rows are read-only mirrors of another phase's task — block
    // every field and point the user at the original. (Shadows never carry
    // `backlogIssueKeys`, so the Backlog branch above is a no-op for them.)
    if (target && isWbsItemShadow(target)) {
      setBacklogLockNotice('参照（シャドウ）行です — 編集は元タスクで行ってください');
      if (lockNoticeTimerRef.current) clearTimeout(lockNoticeTimerRef.current);
      lockNoticeTimerRef.current = setTimeout(
        () => setBacklogLockNotice(null),
        3200,
      );
      return;
    }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setEditing({ itemId, field, anchorRect: rect });
  };

  const closeEdit = () => setEditing(null);

  const commitEdit = async (
    itemId: string,
    field: EditableField,
    raw: string,
  ) => {
    const current = nodes.find((n) => n.node.item.itemId === itemId)?.node.item;
    setEditing(null);
    if (!current) return;
    const patch: UpdateWbsItemPatch = {};
    if (field === 'name') {
      const next = raw.trim();
      if (next === current.name) return;
      if (!next) return;
      patch.name = next;
    } else if (field === 'assigneeId') {
      // The picker emits the empty string for "未割当"; map it to null
      // so Firestore stores an explicit "no assignee" rather than a
      // sentinel string.
      const next = raw === '' ? null : raw;
      if (next === (current.assigneeId ?? null)) return;
      patch.assigneeId = next;
    } else if (field === 'plannedStart' || field === 'plannedEnd') {
      const next = parseDateInput(raw);
      const cur = tsToDate(
        field === 'plannedStart' ? current.plannedStart : current.plannedEnd,
      );
      if ((next?.getTime() ?? null) === (cur?.getTime() ?? null)) return;
      patch[field] = next;
      // Milestones store a single date in both planned* fields. When the
      // user edits plannedStart on a milestone, mirror it into plannedEnd
      // so range-based code paths stay consistent.
      if (current.kind === 'milestone' && field === 'plannedStart') {
        patch.plannedEnd = next;
      }
    } else if (field === 'status') {
      // Only the 5 known WbsItemStatus values; cell editor enforces this
      // but we double-check before persisting to ignore stray empty
      // commits (e.g. the "(未設定)" item from InlineChoice).
      const allowed: readonly WbsItemStatus[] = [
        'not_started',
        'in_progress',
        'done',
        'blocked',
        'cancelled',
      ];
      if (!allowed.includes(raw as WbsItemStatus)) return;
      if (raw === current.status) return;
      patch.status = raw as WbsItemStatus;
    }
    try {
      await onEdit(itemId, patch);
    } catch (err) {
      console.error('[WbsTreeTable] edit failed', err);
    }
  };

  // 領域 is multi-value: its cell editor commits the full label array
  // (not the generic `patch[field] = string` path). Writes only the
  // canonical `systems` field. The popover stays open while editing, so
  // we don't close on commit here.
  const commitSystems = async (itemId: string, next: string[]) => {
    const current = nodes.find((n) => n.node.item.itemId === itemId)?.node.item;
    if (!current) return;
    const prev = readWbsItemSystems(current);
    if (
      prev.length === next.length &&
      prev.every((v, i) => v === next[i])
    ) {
      return;
    }
    try {
      await onEdit(itemId, { systems: next });
    } catch (err) {
      console.error('[WbsTreeTable] systems edit failed', err);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {backlogLockNotice ? (
        <div
          data-testid="wbs-backlog-lock-toast"
          role="status"
          className="pointer-events-none fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-md bg-indigo-700 px-3 py-2 text-xs font-medium text-white shadow-lg"
        >
          {backlogLockNotice}
        </div>
      ) : null}
      <div
        data-testid="wbs-tree-header"
        className="sticky top-0 z-20 grid bg-slate-100 text-xs uppercase tracking-wide text-slate-600"
        style={{ gridTemplateColumns: gridTemplate, height: 48 }}
      >
        {/* Spacer cell for the 24px drag-handle column so subsequent
            headers align with their row data cells. Hidden on mobile /
            tablet because drag reordering is a desktop-only flow. */}
        <div className="hidden border-b border-slate-200 lg:block" />
        <ResizableHeader
          onResize={beginResize('name')}
          className="pl-1"
          leading={
            <NameColumnMenu
              onExpandAll={onExpandAll}
              onCollapseAll={onCollapseAll}
              onCollapsePhaseParents={onCollapsePhaseParents}
            />
          }
        >
          名称
        </ResizableHeader>
        <ResizableHeader
          onResize={beginResize('system')}
          className="hidden lg:flex"
          editAction={{
            active: openEditor === 'system',
            onToggle: () =>
              onOpenEditor(openEditor === 'system' ? null : 'system'),
            label: '領域の選択肢を編集',
          }}
          overlay={openEditor === 'system' ? editorSlot : null}
        >
          領域
        </ResizableHeader>
        <ResizableHeader
          onResize={beginResize('backlogProject')}
          className="hidden lg:flex"
        >
          Backlog
        </ResizableHeader>
        <ResizableHeader
          onResize={beginResize('assignee')}
          className="hidden sm:flex"
          editAction={{
            active: openEditor === 'assignee',
            onToggle: () =>
              onOpenEditor(openEditor === 'assignee' ? null : 'assignee'),
            label: '担当者リストを編集',
          }}
          overlay={openEditor === 'assignee' ? editorSlot : null}
        >
          担当
        </ResizableHeader>
        <ResizableHeader
          onResize={beginResize('plannedStart')}
          className="hidden lg:flex"
        >
          予定開始
        </ResizableHeader>
        <ResizableHeader
          onResize={beginResize('plannedEnd')}
          className="hidden sm:flex"
        >
          予定終了
        </ResizableHeader>
        <HeaderCell>ステータス</HeaderCell>
      </div>
      <DndContext
        sensors={sensors}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        {nodes.map(({ node, hasHiddenChildren, directMatch }, idx) => {
          const rowEditing =
            editing && editing.itemId === node.item.itemId ? editing : null;
          // Insert a vertical gap above every phase row except the very
          // first row, so phase groups read as visually-separated bands.
          // The matching gap on the Gantt side comes from `computeRowTops`.
          const topGap = idx > 0 && node.item.kind === 'phase' ? PHASE_GAP_PX : 0;
          return (
            <DraggableRow
              key={node.item.itemId}
              node={node}
              isExpanded={expanded.has(node.item.itemId)}
              hasHiddenChildren={hasHiddenChildren}
              directMatch={directMatch}
              selected={selectedId === node.item.itemId}
              hovered={hoveredId === node.item.itemId}
              dragging={activeId === node.item.itemId}
              now={now}
              onToggle={onToggle}
              onSelect={onSelect}
              onHover={onHover}
              registerRow={registerRow}
              gridTemplate={gridTemplate}
              editing={rowEditing}
              onOpenEdit={openEdit}
              onCommitEdit={commitEdit}
              onCommitSystems={commitSystems}
              onCancelEdit={closeEdit}
              areaOptions={areaOptions}
              areaColorIndices={areaColorIndices}
              assigneeOptions={assigneeOptions}
              backlogSpaceUrl={backlogSpaceUrl}
              topGap={topGap}
            />
          );
        })}
        <DragOverlay dropAnimation={null}>
          {activeNode ? <OverlayRow node={activeNode.node} gridTemplate={gridTemplate} /> : null}
        </DragOverlay>
      </DndContext>
      {nodes.length > 0 ? (
        <div aria-hidden style={{ height: BOTTOM_PAD_PX }} />
      ) : null}
      {indicator ? <DropIndicator indicator={indicator} /> : null}
    </div>
  );
}

/**
 * Container-level drop feedback. One element for the whole tree — no
 * more stitching separate "before" / "after" slivers onto individual
 * rows, which used to flicker at row boundaries.
 */
function DropIndicator({ indicator }: { indicator: Indicator }) {
  if (indicator.kind === 'inside') {
    return (
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-0 z-40 rounded-sm border-2',
          indicator.invalid
            ? 'border-red-400 bg-red-50/40'
            : 'border-sky-400 bg-sky-50/40',
        )}
        style={{ top: indicator.top, height: indicator.height }}
      />
    );
  }
  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute right-0 z-40 h-0.5 -translate-y-1/2 transition-[top,left] duration-75',
        indicator.invalid ? 'bg-red-500' : 'bg-sky-500',
      )}
      style={{ top: indicator.top, left: indicator.leftIndent }}
    >
      <span
        className={cn(
          'absolute left-0 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full',
          indicator.invalid ? 'bg-red-500' : 'bg-sky-500',
        )}
      />
    </div>
  );
}

function HeaderCell({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center overflow-hidden whitespace-nowrap border-b border-slate-200 px-2 text-[11px] font-medium',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Header cell with a draggable right-edge divider. When `editAction`
 * is present a ⋯ button sits between the label and the resize handle
 * so the user can open a column-specific options editor. `overlay` is
 * anchored under the cell and forwarded from the parent (the parent
 * owns popover state so saving can apply batch migrations).
 */
function ResizableHeader({
  children,
  onResize,
  className,
  leading,
  editAction,
  overlay,
}: {
  children: React.ReactNode;
  onResize: (e: React.MouseEvent) => void;
  className?: string;
  /** Optional control rendered to the LEFT of the header label. */
  leading?: React.ReactNode;
  editAction?: {
    active: boolean;
    onToggle: () => void;
    label: string;
  };
  overlay?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'relative flex items-center border-b border-slate-200 px-2 text-[11px] font-medium',
        className,
      )}
    >
      {leading ? <span className="mr-1 shrink-0">{leading}</span> : null}
      <span className="min-w-0 truncate">{children}</span>
      {editAction ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            editAction.onToggle();
          }}
          aria-label={editAction.label}
          aria-pressed={editAction.active}
          className={cn(
            'ml-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-slate-200 hover:text-slate-700',
            editAction.active ? 'bg-slate-200 text-slate-900' : '',
          )}
        >
          <MoreHorizontal className="h-3 w-3" />
        </button>
      ) : null}
      <div
        role="separator"
        aria-label="列幅をドラッグして変更"
        onMouseDown={onResize}
        onClick={(e) => e.stopPropagation()}
        className="group absolute right-0 top-0 flex h-full w-2 cursor-col-resize items-center justify-center"
      >
        <div className="h-4 w-px bg-slate-300 group-hover:bg-slate-500" />
      </div>
      {overlay}
    </div>
  );
}

/**
 * Presentational list of the three expand/collapse menu items. Split out
 * from {@link NameColumnMenu} (which owns the open/close state) so it can
 * be rendered + asserted in a `node`-env test without simulating clicks.
 */
export function NameColumnMenuList({
  onExpandAll,
  onCollapseAll,
  onCollapsePhaseParents,
  onAfter,
}: {
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onCollapsePhaseParents: () => void;
  /** Run after any item fires (the menu uses this to close itself). */
  onAfter?: () => void;
}) {
  const run = (fn: () => void) => () => {
    fn();
    onAfter?.();
  };
  return (
    <>
      <NameMenuItem
        testId="wbs-name-menu-expand-all"
        onClick={run(onExpandAll)}
        label="全て展開"
      />
      <NameMenuItem
        testId="wbs-name-menu-collapse-all"
        onClick={run(onCollapseAll)}
        label="全て折りたたむ"
      />
      <NameMenuItem
        testId="wbs-name-menu-collapse-phase-parents"
        onClick={run(onCollapsePhaseParents)}
        label="フェーズ内親タスクのみ折りたたむ"
      />
    </>
  );
}

function NameMenuItem({
  onClick,
  label,
  testId,
}: {
  onClick: () => void;
  label: string;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="inline-flex w-full items-center whitespace-nowrap rounded px-2 py-1.5 text-left text-xs font-normal normal-case text-slate-700 hover:bg-slate-100"
    >
      {label}
    </button>
  );
}

/**
 * Icon dropdown anchored to the left of the 名称 column header. Bundles
 * the expand / collapse actions that used to live in the page toolbar:
 *   - 全て展開
 *   - 全て折りたたむ
 *   - フェーズ内親タスクのみ折りたたむ
 *
 * Esc and outside-click dismissal come from {@link useDismissable}.
 */
function NameColumnMenu({
  onExpandAll,
  onCollapseAll,
  onCollapsePhaseParents,
}: {
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onCollapsePhaseParents: () => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  useDismissable({
    open,
    onClose: () => setOpen(false),
    containerRef: panelRef,
    triggerRef,
  });
  return (
    <span className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        data-testid="wbs-name-menu-trigger"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label="展開 / 折りたたみメニュー"
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-200 hover:text-slate-700',
          open ? 'bg-slate-200 text-slate-900' : '',
        )}
      >
        <ListTree className="h-3.5 w-3.5" />
      </button>
      {open ? (
        <div
          ref={panelRef}
          data-testid="wbs-name-menu"
          role="menu"
          tabIndex={-1}
          className="absolute left-0 top-full z-40 mt-1 flex w-56 flex-col gap-0.5 rounded-md border border-slate-200 bg-white p-1 shadow-lg"
        >
          <NameColumnMenuList
            onExpandAll={onExpandAll}
            onCollapseAll={onCollapseAll}
            onCollapsePhaseParents={onCollapsePhaseParents}
            onAfter={() => setOpen(false)}
          />
        </div>
      ) : null}
    </span>
  );
}

function DraggableRow(props: {
  node: WbsTreeNode;
  isExpanded: boolean;
  hasHiddenChildren: boolean;
  directMatch: boolean;
  selected: boolean;
  hovered: boolean;
  dragging: boolean;
  now: Date;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
  onHover?: (id: string | null) => void;
  registerRow: (id: string, el: HTMLDivElement | null) => void;
  gridTemplate: string;
  editing: EditTarget | null;
  onOpenEdit: (id: string, field: EditableField, e: React.MouseEvent) => void;
  onCommitEdit: (
    id: string,
    field: EditableField,
    raw: string,
  ) => Promise<void> | void;
  /** Multi-value 領域 commit — writes the full label array. */
  onCommitSystems: (id: string, next: string[]) => Promise<void> | void;
  onCancelEdit: () => void;
  areaOptions: readonly string[];
  areaColorIndices?: AreaColorIndexMap;
  /**
   * Project-wide assignee list (the same list shared by the
   * migration-task DAG view and the Settings page). The picker writes
   * the selected `assigneeId` back to the row; the cell renders the
   * doc's name + frozen color.
   */
  assigneeOptions: readonly AssigneeDoc[];
  backlogSpaceUrl: string | null;
  /** Pixels of empty space to render above this row (phase-section gap). */
  topGap: number;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } =
    useDraggable({ id: props.node.item.itemId });

  const compose = (el: HTMLDivElement | null) => {
    setNodeRef(el);
    props.registerRow(props.node.item.itemId, el);
  };

  return (
    <Row
      {...props}
      rowRef={compose}
      dragging={props.dragging || isDragging}
      dragHandleProps={{
        ref: setActivatorNodeRef,
        listeners,
        attributes,
      }}
    />
  );
}

interface DragHandleProps {
  ref: (el: HTMLElement | null) => void;
  listeners: ReturnType<typeof useDraggable>['listeners'];
  attributes: ReturnType<typeof useDraggable>['attributes'];
}

function Row({
  node,
  isExpanded,
  hasHiddenChildren,
  directMatch,
  selected,
  hovered,
  dragging,
  now,
  onToggle,
  onSelect,
  onHover,
  rowRef,
  dragHandleProps,
  gridTemplate,
  editing,
  onOpenEdit,
  onCommitEdit,
  onCommitSystems,
  onCancelEdit,
  areaOptions,
  areaColorIndices,
  assigneeOptions,
  backlogSpaceUrl,
  topGap,
}: {
  node: WbsTreeNode;
  isExpanded: boolean;
  hasHiddenChildren: boolean;
  directMatch: boolean;
  selected: boolean;
  hovered: boolean;
  dragging: boolean;
  now: Date;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
  onHover?: (id: string | null) => void;
  rowRef?: (el: HTMLDivElement | null) => void;
  dragHandleProps?: DragHandleProps;
  gridTemplate: string;
  editing: EditTarget | null;
  onOpenEdit: (id: string, field: EditableField, e: React.MouseEvent) => void;
  onCommitEdit: (
    id: string,
    field: EditableField,
    raw: string,
  ) => Promise<void> | void;
  /** Multi-value 領域 commit — writes the full label array. */
  onCommitSystems: (id: string, next: string[]) => Promise<void> | void;
  onCancelEdit: () => void;
  areaOptions: readonly string[];
  areaColorIndices?: AreaColorIndexMap;
  /**
   * Project-wide assignee list (the same list shared by the
   * migration-task DAG view and the Settings page). The picker writes
   * the selected `assigneeId` back to the row; the cell renders the
   * doc's name + frozen color.
   */
  assigneeOptions: readonly AssigneeDoc[];
  backlogSpaceUrl: string | null;
  topGap: number;
}) {
  const item = node.item;
  const delayed = classifyDelay(item, now);
  const isMilestone = item.kind === 'milestone';
  const start = tsToDate(item.plannedStart);
  const end = tsToDate(item.plannedEnd);
  const hasChildren = node.children.length > 0;
  // Multi-value 領域 for this row (canonical `systems`, legacy fallback).
  const itemSystems = readWbsItemSystems(item);

  const activeField = editing?.field ?? null;
  const anchorRect = editing?.anchorRect ?? null;

  const areaChoices: ChoiceOption[] = useMemo(
    () =>
      areaOptions.map((v) => ({
        value: v,
        label: v,
        badgeClasses: areaBadgeClasses(v, areaColorIndices),
      })),
    [areaOptions, areaColorIndices],
  );
  // Backlog project key derived from the row's primary issue key; null
  // for rows with no Backlog issues. We display the bare key only —
  // column width is tight and the full display name pushes everything
  // else off-screen.
  const backlogProjectKey = useMemo(
    () => primaryBacklogProjectKey(item),
    [item],
  );
  // `assigneeChoices` carries the canonical project-wide list — the
  // picker value is `assigneeId`, the label is the doc name, and the
  // badge color tracks each doc's frozen `colorIndex` so a row's chip
  // matches the same person's chip on the DAG / settings page.
  // A leading "未割当" entry lets the user clear an existing link
  // without typing.
  const assigneeChoices: ChoiceOption[] = useMemo(
    () => [
      {
        value: '',
        label: '— 未割当 —',
        badgeClasses: 'bg-slate-100 text-slate-500 border border-slate-200',
      },
      ...assigneeOptions.map((a) => ({
        value: a.assigneeId,
        label: a.name,
        badgeClasses: resolveAssigneeColorClasses(a, a.name),
      })),
    ],
    [assigneeOptions],
  );
  // Resolve the current row's assignee doc once so the cell renders
  // the canonical name + color rather than the legacy free-text label.
  const currentAssignee = useMemo(
    () =>
      item.assigneeId
        ? assigneeOptions.find((a) => a.assigneeId === item.assigneeId) ?? null
        : null,
    [assigneeOptions, item.assigneeId],
  );

  // Shadow (参照) rows mirror another phase's task; rendered read-only with
  // a jump-to-original affordance.
  const isShadow = isWbsItemShadow(item);

  return (
    <div
      ref={rowRef}
      data-wbs-item-id={item.itemId}
      style={{
        gridTemplateColumns: gridTemplate,
        height: ROW_HEIGHT,
        marginTop: topGap || undefined,
        // Sticky phase rows hang just below the column-header band (48px
        // tall, see line 700) so the user always knows which phase they
        // are reading. Plain task rows scroll normally.
        ...(item.kind === 'phase'
          ? { position: 'sticky' as const, top: 48 }
          : {}),
      }}
      onClick={() => onSelect(item.itemId)}
      onMouseEnter={onHover ? () => onHover(item.itemId) : undefined}
      onMouseLeave={onHover ? () => onHover(null) : undefined}
      className={cn(
        'relative grid cursor-pointer items-center border-b border-slate-100 text-sm',
        // Phase rows act as section headers: a visible top border draws
        // the start-of-section line across the row, and a subtle tinted
        // background distinguishes the header from its child task rows.
        // Sticky stack order: column headers (z-20) > phase rows (z-10)
        // > task rows (default). A subtle bottom shadow appears once the
        // row is pinned to soften the join with the content below.
        item.kind === 'phase'
          ? 'z-10 border-t-2 border-t-slate-200 shadow-[0_1px_0_rgba(15,23,42,0.06)]'
          : '',
        selected
          ? 'bg-slate-200'
          : // Visual shade gradient: white (active task) → slate-50
            // (completed task) → slate-100 (phase header) → slate-200
            // (hover / selected). Keeps phase rows clearly distinct
            // from completed-task rows.
            item.kind === 'task' && item.status === 'done'
            ? hovered
              ? 'bg-slate-100'
              : 'bg-slate-50'
            : hovered
            ? item.kind === 'phase'
              ? 'bg-slate-200'
              : 'bg-slate-50'
            : item.kind === 'phase'
            ? 'bg-slate-100 hover:bg-slate-200'
            : 'bg-white hover:bg-slate-50',
        directMatch ? '' : 'opacity-70',
        dragging ? 'opacity-40' : '',
        // Backlog-mastered rows get a left accent so the lock reads at a
        // glance in the tree.
        isWbsItemBacklogLinked(item) ? 'border-l-2 border-l-indigo-300' : '',
        // Shadow (参照) rows are read-only mirrors of another phase's task —
        // greyed + faded so they read as secondary, with a slate accent.
        isShadow ? 'border-l-2 border-l-slate-300 bg-slate-50 opacity-60' : '',
      )}
    >
      <div
        ref={dragHandleProps?.ref as React.Ref<HTMLDivElement>}
        {...(dragHandleProps?.listeners ?? {})}
        {...(dragHandleProps?.attributes ?? {})}
        onClick={(e) => e.stopPropagation()}
        className="hidden h-full cursor-grab items-center justify-center text-slate-300 hover:text-slate-500 active:cursor-grabbing lg:flex"
        aria-label="行をドラッグして並び替え"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </div>
      <div
        className="flex items-center gap-1 overflow-hidden pl-2"
        style={{ paddingLeft: 8 + node.depth * 14 }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(item.itemId);
            }}
            className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-200"
            aria-label={isExpanded ? '折りたたむ' : '展開する'}
          >
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </button>
        ) : isMilestone ? (
          // ◆ glyph in the gutter so milestones read as marker rows at
          // a glance even before the eye lands on the title text.
          <span
            className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-amber-600"
            aria-hidden
          >
            ◆
          </span>
        ) : (
          <span className="inline-block w-4" />
        )}
        {activeField === 'name' ? (
          <InlineText
            value={item.name}
            onCommit={(v) => onCommitEdit(item.itemId, 'name', v)}
            onCancel={onCancelEdit}
          />
        ) : (
          <span
            onClick={(e) => {
              e.stopPropagation();
              onSelect(item.itemId);
              onOpenEdit(item.itemId, 'name', e);
            }}
            className={cn(
              'cursor-text truncate rounded px-0.5 hover:bg-slate-200/60',
              delayed ? 'text-red-700' : 'text-slate-900',
              item.kind === 'phase' ? 'font-semibold' : '',
              isShadow ? 'italic text-slate-500' : '',
            )}
            title={item.name}
          >
            {isShadow ? '(参照) ' : ''}
            {item.name || '(無題)'}
          </span>
        )}
        {isShadow && item.shadowOf ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // Navigate to + select the original task (in its own phase).
              onSelect(item.shadowOf as string);
            }}
            className="inline-flex shrink-0 items-center gap-0.5 rounded px-1 text-[10px] text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
            title="元タスクへ移動"
          >
            ↗ 元タスクへ
          </button>
        ) : null}
        {item.backlogIssueKeys.length > 0 ? (
          <BacklogJumpLink
            issueKeys={item.backlogIssueKeys}
            spaceUrl={backlogSpaceUrl}
          />
        ) : null}
        <BacklogMilestoneBadge item={item} />
        {hasHiddenChildren && !isExpanded ? (
          <span className="ml-1 text-[10px] text-slate-400">…</span>
        ) : null}
      </div>
      <EditableCell
        onOpen={(e) => onOpenEdit(item.itemId, 'system', e)}
        suppressRowSelect
        className="hidden lg:flex"
      >
        {itemSystems.length > 0 ? (
          <span className="flex w-full flex-nowrap items-center justify-start gap-0.5 overflow-hidden">
            {itemSystems.map((s) => (
              <span
                key={s}
                className={cn(
                  'inline-flex shrink-0 items-center whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-medium',
                  areaBadgeClasses(s, areaColorIndices),
                )}
                title={s}
              >
                {s}
              </span>
            ))}
          </span>
        ) : (
          <span className="block text-left text-xs text-slate-400">—</span>
        )}
        {activeField === 'system' && anchorRect ? (
          <InlineMultiChoice
            options={areaChoices}
            value={itemSystems}
            anchorRect={anchorRect}
            onCommit={(next) => onCommitSystems(item.itemId, next)}
            onCancel={onCancelEdit}
          />
        ) : null}
      </EditableCell>
      {/*
       * Read-only Backlog project column. Renders the bare key (e.g.
       * `CIS_TTM`) left-aligned; the display name is intentionally not
       * resolved because the column is narrow.
       */}
      <div
        className="hidden min-w-0 items-center px-2 lg:flex"
        onClick={(e) => e.stopPropagation()}
      >
        {backlogProjectKey ? (
          <span
            className={cn(
              'inline-flex max-w-full items-center justify-start truncate rounded-md px-1.5 py-0.5 text-[11px] font-medium',
              areaBadgeClasses(backlogProjectKey),
            )}
            title={backlogProjectKey}
          >
            {backlogProjectKey}
          </span>
        ) : (
          <span className="block w-full text-left text-xs text-slate-400">
            —
          </span>
        )}
      </div>
      <EditableCell
        onOpen={(e) => onOpenEdit(item.itemId, 'assigneeId', e)}
        suppressRowSelect
        className="hidden sm:flex"
      >
        {currentAssignee ? (
          // Render with the canonical name + frozen colorIndex so renames
          // in Settings propagate immediately and rows for the same
          // person never disagree on color.
          <AssigneeChip
            assignee={currentAssignee}
            size="sm"
            className="w-full justify-center"
          />
        ) : (
          <span className="block text-center text-xs text-slate-400">—</span>
        )}
        {activeField === 'assigneeId' && anchorRect ? (
          <InlineChoice
            options={assigneeChoices}
            value={item.assigneeId ?? ''}
            anchorRect={anchorRect}
            onCommit={(v) => onCommitEdit(item.itemId, 'assigneeId', v)}
            onCancel={onCancelEdit}
          />
        ) : null}
      </EditableCell>
      <EditableCell
        onOpen={(e) => onOpenEdit(item.itemId, 'plannedStart', e)}
        className="hidden px-1 lg:flex"
      >
        {activeField === 'plannedStart' ? (
          <InlineDate
            value={toDateInput(start)}
            onCommit={(v) => onCommitEdit(item.itemId, 'plannedStart', v)}
            onCancel={onCancelEdit}
          />
        ) : start ? (
          <span className="block truncate text-xs text-slate-700">
            {format(start, 'yyyy/MM/dd')}
          </span>
        ) : (
          <span className="block text-center text-xs text-slate-400">—</span>
        )}
      </EditableCell>
      {isMilestone ? (
        // Milestones own a single date — the end column is meaningless
        // and would invite confusing edits where start≠end. Show an
        // explicit "—" and don't open the editor.
        <div className="hidden min-w-0 items-center justify-center px-2 sm:flex">
          <span className="text-xs text-slate-400">—</span>
        </div>
      ) : (
        <EditableCell
          onOpen={(e) => onOpenEdit(item.itemId, 'plannedEnd', e)}
          className="hidden px-1 sm:flex"
        >
          {activeField === 'plannedEnd' ? (
            <InlineDate
              value={toDateInput(end)}
              onCommit={(v) => onCommitEdit(item.itemId, 'plannedEnd', v)}
              onCancel={onCancelEdit}
            />
          ) : end ? (
            <span
              className={cn(
                'block truncate text-xs',
                // Past-due rows (plannedEnd in the past, not terminal)
                // get red text so the delay is visible at the date cell
                // itself — matches the existing name-column treatment.
                delayed ? 'font-semibold text-red-700' : 'text-slate-700',
              )}
            >
              {format(end, 'yyyy/MM/dd')}
            </span>
          ) : (
            <span className="block text-center text-xs text-slate-400">—</span>
          )}
        </EditableCell>
      )}
      {/*
       * Status cell:
       *   - Leaf tasks / milestones → click to open the inline picker.
       *   - Phases or rows with children → locked. The Cloud Function
       *     `aggregateWbsParent` rolls children's statuses up into the
       *     parent (see `rollupStatus`), so direct edits would be
       *     overwritten on the next child write.
       */}
      {item.kind === 'phase' || hasChildren ? (
        <div
          className="flex min-w-0 justify-center px-2"
          title="子行から自動集計 — 直接変更できません"
        >
          <Badge
            tone={STATUS_TONE[item.status]}
            className="w-full cursor-not-allowed justify-center opacity-70"
          >
            {STATUS_LABEL[item.status]}
          </Badge>
        </div>
      ) : (
        <EditableCell
          onOpen={(e) => onOpenEdit(item.itemId, 'status', e)}
          className="!px-2"
          suppressRowSelect
        >
          <Badge
            tone={STATUS_TONE[item.status]}
            className="w-full justify-center"
          >
            {STATUS_LABEL[item.status]}
          </Badge>
          {activeField === 'status' && anchorRect ? (
            <InlineChoice
              options={STATUS_CHOICES}
              value={item.status}
              anchorRect={anchorRect}
              allowClear={false}
              onCommit={(v) => onCommitEdit(item.itemId, 'status', v)}
              onCancel={onCancelEdit}
            />
          ) : null}
        </EditableCell>
      )}
    </div>
  );
}

/**
 * Clickable wrapper for an editable grid cell.
 *
 * For most cells (date columns, etc.) the click bubbles up to the row's
 * `onClick` so the right-hand detail panel also opens — that's the
 * existing "click row to view details" interaction.
 *
 * For dropdown-style cells (領域 / ベンダー / 担当 / ステータス) we pass
 * `suppressRowSelect` so the inline picker opens WITHOUT also sliding
 * in the detail panel. The user's intent there is to edit the value,
 * not to navigate; opening the panel as a side effect was reported as
 * noisy. If the panel is already open it remains open (we never clear
 * `selectedId`), which matches the requested behaviour.
 */
function EditableCell({
  children,
  onOpen,
  className,
  suppressRowSelect = false,
}: {
  children: React.ReactNode;
  onOpen: (e: React.MouseEvent) => void;
  className?: string;
  /**
   * When true, `e.stopPropagation()` is called before `onOpen` so the
   * row's `onClick` (which calls `onSelect` / opens the detail panel)
   * is bypassed. Set this on dropdown / popover trigger cells.
   */
  suppressRowSelect?: boolean;
}) {
  // Marker attribute lets unit tests assert which cells suppress
  // propagation without simulating a real DOM click in jsdom.
  const dataAttrs = suppressRowSelect
    ? { 'data-suppress-row-select': 'true' as const }
    : {};
  return (
    <div
      onClick={createEditableCellClickHandler(onOpen, suppressRowSelect)}
      {...dataAttrs}
      className={cn(
        'relative flex min-w-0 cursor-pointer items-center px-2',
        className,
      )}
    >
      {children}
    </div>
  );
}

function OverlayRow({ node, gridTemplate }: { node: WbsTreeNode; gridTemplate: string }) {
  const item = node.item;
  return (
    <div
      className="grid items-center rounded border border-sky-300 bg-white/90 text-sm shadow-lg"
      style={{ gridTemplateColumns: gridTemplate, height: ROW_HEIGHT }}
    >
      <div className="flex h-full items-center justify-center text-slate-400">
        <GripVertical className="h-3.5 w-3.5" />
      </div>
      <div
        className="flex items-center gap-1 overflow-hidden pl-2"
        style={{ paddingLeft: 8 + node.depth * 14 }}
      >
        {item.kind === 'milestone' ? (
          <span
            className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-amber-600"
            aria-hidden
          >
            ◆
          </span>
        ) : null}
        <span
          className={cn(
            'truncate',
            item.kind === 'phase' ? 'font-semibold' : '',
          )}
        >
          {item.name || '(無題)'}
        </span>
      </div>
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
    </div>
  );
}

/**
 * Tiny external-link icon shown next to the name when a row has linked
 * Backlog issues. Click jumps to the first issue in a new tab; hover
 * shows all keys. Stops propagation so it doesn't fight the row's
 * select / inline-edit handlers.
 */
/**
 * Compact inline badges that surface the Backlog-link state on a WBS row:
 *
 *   - `kind === 'phase'` with one or more milestone / parent-issue links →
 *     "🔗 連携中" pill (suffixed with the count when N > 1).
 *   - An origin row (`backlogMilestoneOrigin` / `backlogParentIssueOrigin` /
 *     `backlogCategoryOrigin`) with EMPTY `backlogIssueKeys` →
 *     "⚠ M外 / P外 / C外" pill (light red): the row was created by a link
 *     but its source issues have since left the milestone / parent / category.
 *
 * Provenance ("由来") for rows that still belong is shown only in the detail
 * panel, never in the list — see `BacklogOriginSection` in WbsItemDetailPanel.
 */
function BacklogMilestoneBadge({ item }: { item: WbsItemDoc }) {
  if (item.kind === 'phase') {
    const mLinks = item.backlogMilestoneLinks ?? [];
    const pLinks = item.backlogParentIssueLinks ?? [];
    const total = mLinks.length + pLinks.length;
    if (total > 0) {
      const titleLines = [
        ...mLinks.map(
          (l) =>
            `マイルストン: ${l.backlogProjectName || l.backlogProjectKey} / ${l.milestoneName}`,
        ),
        ...pLinks.map(
          (l) =>
            `親課題: ${l.backlogProjectName || l.backlogProjectKey} / ${l.parentIssueKey}`,
        ),
      ];
      return (
        <span
          data-testid="wbs-row-milestone-link-badge"
          title={`連携中:\n${titleLines.join('\n')}`}
          className="ml-1 inline-flex shrink-0 items-center rounded bg-sky-50 px-1 py-[1px] text-[10px] font-medium text-sky-700"
        >
          🔗 連携中{total > 1 ? ` (${total})` : ''}
        </span>
      );
    }
  }
  // Provenance ("由来") badges are intentionally NOT shown in the list —
  // they live in the detail panel. The list only surfaces the "外れ" (orphan)
  // warning: a row whose source issues have all left the milestone / parent
  // issue / category (empty `backlogIssueKeys`). Tinted light red so a
  // drifted row stands out.
  const orphaned = (item.backlogIssueKeys ?? []).length === 0;
  if (!orphaned) return null;
  const orphanClass =
    'ml-1 inline-flex shrink-0 items-center rounded border border-red-200 bg-red-50 px-1 py-[1px] text-[10px] font-medium text-red-700';
  const mOrigin = item.backlogMilestoneOrigin;
  if (mOrigin) {
    return (
      <span
        data-testid="wbs-row-milestone-orphan-badge"
        title={`元連携: ${mOrigin.milestoneName}（マイルストン外）`}
        className={orphanClass}
      >
        ⚠ M外
      </span>
    );
  }
  const pOrigin = item.backlogParentIssueOrigin;
  if (pOrigin) {
    return (
      <span
        data-testid="wbs-row-parent-orphan-badge"
        title={`元連携: ${pOrigin.parentIssueKey}（親課題の子課題外）`}
        className={orphanClass}
      >
        ⚠ P外
      </span>
    );
  }
  const cOrigin = item.backlogCategoryOrigin;
  if (cOrigin) {
    return (
      <span
        data-testid="wbs-row-category-orphan-badge"
        title={`元連携: ${cOrigin.categoryName}（カテゴリ外）`}
        className={orphanClass}
      >
        ⚠ C外
      </span>
    );
  }
  return null;
}

function BacklogJumpLink({
  issueKeys,
  spaceUrl,
}: {
  issueKeys: readonly string[];
  spaceUrl: string | null;
}) {
  const first = issueKeys[0];
  if (!first) return null;
  const url = buildBacklogIssueUrl(spaceUrl, first);
  if (!url) return null;
  const label =
    issueKeys.length === 1
      ? `Backlog: ${first}`
      : `Backlog: ${issueKeys.join(', ')}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      className="ml-auto inline-flex shrink-0 items-center pl-2 text-slate-400 hover:text-blue-600"
      title={label}
      aria-label={label}
    >
      <ExternalLink className="h-3 w-3" />
      {issueKeys.length > 1 ? (
        <span className="ml-0.5 text-[10px] tabular-nums">
          {issueKeys.length}
        </span>
      ) : null}
    </a>
  );
}
